import { activeSpeakerReset } from 'features/streaming/streamingSlice';
import { captionsToggled } from 'features/transcripts/slices/captions/captionsSlice';
import { chatMessagesReset } from 'features/chat/chatSlice';
import { streamMinimized } from 'features/layout/actions';
import {
  gridPanelClosed,
  lockedContentChanged,
  selectMaximizedStream,
} from 'features/layout/features/content/contentSlice';
import { SourceDetails } from 'features/layout/types';
import { closeModal, selectActiveModal } from 'features/modal/modalSlice';
import { ModalName } from 'features/modal/types';
import { clearAllNotificationsSaga } from 'features/notifications/sagas/clearAllNotificationsSaga';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import { broadcastSetupEnded, selectIsBroadcastSetupActive } from 'features/room/roomSlice';
import { userListReset, userReactionReset } from 'features/users/usersSlice';
import { selectGridPanelOpen } from 'features/whiteboard/whiteboardSlice';
import { call, put, select } from 'redux-saga/effects';
import { RTCClient } from 'utils/webrtc';
import {
  activeFilesCollectionCleared,
  activeWhiteboardsCollectionCleared,
  openedFileCleared,
} from 'features/content-library/contentLibrarySlice';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';
import { E2EEManager } from 'features/e2ee/E2EEManager';
import { SFrameManager } from 'features/e2ee/SFrameManager';
import { cleanBreakoutRooms } from 'features/breakout-rooms/breakoutRoomsSlice';
import { externalStreamsCleared } from 'features/layout/features/order/orderSlice';
import { contentBoard } from 'features/content-whiteboard/utils/contentBoardStateManager';
import { contentLibraryFilePreviewClosed } from 'features/content-library/actions';

// fire to set up movement between breakout/main rooms;
export function* prepareRoomMovementSaga() {
  yield put(cleanBreakoutRooms());

  const isBroadcastSetupActive: boolean = yield select(selectIsBroadcastSetupActive);
  if (isBroadcastSetupActive) {
    yield put(broadcastSetupEnded());
  }

  const gridPanelOpen: boolean = yield select(selectGridPanelOpen);
  if (gridPanelOpen) {
    yield put(gridPanelClosed());
  }

  yield put(openedFileCleared());
  yield put(activeFilesCollectionCleared());
  yield put(activeWhiteboardsCollectionCleared());
  yield put(externalStreamsCleared());

  yield put(captionsToggled(false));

  yield call(contentBoard.reset);

  yield put(lockedContentChanged(null));

  yield put(contentLibraryFilePreviewClosed());

  const maximizedStream: SourceDetails = yield select(selectMaximizedStream);

  if (maximizedStream) {
    yield put(streamMinimized());
  }

  yield put(chatMessagesReset());
  yield put(activeSpeakerReset());

  const activeModal: ModalName | null = yield select(selectActiveModal);
  if (activeModal) {
    yield put(closeModal(activeModal));
  }

  const preserveMediaStates: boolean = yield call(
    hasPermissionsSaga,
    [PermissionTypes.broadcastVideo, PermissionTypes.broadcastAudio],
    {
      mode: 'any',
    }
  );

  yield call(RTCClient.reset, preserveMediaStates);
  const e2eeEnabled: boolean = yield select(selectE2eeEnabled);
  if (e2eeEnabled) {
    yield call(SFrameManager.cleanup);
    yield call(E2EEManager.cleanup);
  }

  yield put(userListReset());
  yield put(userReactionReset());

  yield call(clearAllNotificationsSaga);
}
